/**
 * Chat Model for V2 Services
 */

export type MessageType = 'text' | 'image' | 'file' | 'booking_update' | 'system';
export type ChatType = 'customer_support' | 'booking_related' | 'general';
export type ChatStatus = 'open' | 'in_progress' | 'resolved' | 'closed';

export interface MessageData {
  id?: string;
  chatId: string;
  senderId: string;
  senderType: 'user' | 'admin' | 'system' | 'ai';
  type: MessageType;
  content: string;
  attachments?: string[];
  metadata?: Record<string, any>;
  readBy: string[];
  createdAt: Date;
}

export interface ChatData {
  id?: string;
  userId: string;
  adminId?: string;
  type: ChatType;
  status: ChatStatus;
  subject?: string;
  bookingId?: string;
  lastMessage?: string;
  lastMessageAt?: Date;
  unreadCount: number;
  priority: 'low' | 'medium' | 'high';
  metadata?: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

export class Message {
  public id?: string;
  public chatId: string;
  public senderId: string;
  public senderType: 'user' | 'admin' | 'system' | 'ai';
  public type: MessageType;
  public content: string;
  public attachments: string[];
  public metadata: Record<string, any>;
  public readBy: string[];
  public createdAt: Date;

  constructor(data: Partial<MessageData>) {
    this.id = data.id;
    this.chatId = data.chatId || '';
    this.senderId = data.senderId || '';
    this.senderType = data.senderType || 'user';
    this.type = data.type || 'text';
    this.content = data.content || '';
    this.attachments = data.attachments || [];
    this.metadata = data.metadata || {};
    this.readBy = data.readBy || [];
    this.createdAt = data.createdAt || new Date();
  }

  public toFirestore(): Record<string, any> {
    return {
      chatId: this.chatId,
      senderId: this.senderId,
      senderType: this.senderType,
      type: this.type,
      text: this.content, // Use 'text' field to match Landing structure
      attachments: this.attachments,
      metadata: this.metadata,
      readBy: this.readBy,
      createdAt: this.createdAt
    };
  }

  public static fromFirestore(data: any): Message {
    return new Message({
      ...data,
      content: data.text || data.content, // Read from 'text' field (Landing structure) or fallback to 'content'
      createdAt: data.createdAt?.toDate?.() || data.createdAt
    });
  }

  public markAsRead(userId: string): void {
    if (!this.readBy.includes(userId)) {
      this.readBy.push(userId);
    }
  }

  public isReadBy(userId: string): boolean {
    return this.readBy.includes(userId);
  }
}

export class Chat {
  public id?: string;
  public userId: string;
  public adminId?: string;
  public type: ChatType;
  public status: ChatStatus;
  public subject?: string;
  public bookingId?: string;
  public lastMessage?: string;
  public lastMessageAt?: Date;
  public unreadCount: number;
  public priority: 'low' | 'medium' | 'high';
  public metadata: Record<string, any>;
  public createdAt: Date;
  public updatedAt: Date;

  constructor(data: Partial<ChatData>) {
    this.id = data.id;
    this.userId = data.userId || '';
    this.adminId = data.adminId;
    this.type = data.type || 'general';
    this.status = data.status || 'open';
    this.subject = data.subject;
    this.bookingId = data.bookingId;
    this.lastMessage = data.lastMessage;
    this.lastMessageAt = data.lastMessageAt;
    this.unreadCount = data.unreadCount || 0;
    this.priority = data.priority || 'medium';
    this.metadata = data.metadata || {};
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
  }

  public toFirestore(): Record<string, any> {
    const data: Record<string, any> = {
      userId: this.userId,
      type: this.type,
      status: this.status,
      unreadCount: this.unreadCount,
      priority: this.priority,
      metadata: this.metadata,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };

    // Only include optional fields if they have values (Firestore doesn't accept undefined)
    if (this.adminId !== undefined) data.adminId = this.adminId;
    if (this.subject !== undefined) data.subject = this.subject;
    if (this.bookingId !== undefined) data.bookingId = this.bookingId;
    if (this.lastMessage !== undefined) data.lastMessage = this.lastMessage;
    if (this.lastMessageAt !== undefined) data.lastMessageAt = this.lastMessageAt;

    return data;
  }

  public static fromFirestore(data: any): Chat {
    return new Chat({
      ...data,
      createdAt: data.createdAt?.toDate?.() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
      lastMessageAt: data.lastMessageAt?.toDate?.() || data.lastMessageAt
    });
  }

  public assignToAdmin(adminId: string): void {
    this.adminId = adminId;
    this.status = 'in_progress';
    this.updatedAt = new Date();
  }

  public updateLastMessage(content: string): void {
    this.lastMessage = content;
    this.lastMessageAt = new Date();
    this.updatedAt = new Date();
  }

  public updateStatus(status: ChatStatus): void {
    this.status = status;
    this.updatedAt = new Date();
  }

  public incrementUnreadCount(): void {
    this.unreadCount++;
    this.updatedAt = new Date();
  }

  public resetUnreadCount(): void {
    this.unreadCount = 0;
    this.updatedAt = new Date();
  }

  public updatePriority(priority: 'low' | 'medium' | 'high'): void {
    this.priority = priority;
    this.updatedAt = new Date();
  }
}